import axios from "axios";
import { useAtom, useSetAtom } from "jotai";
import { environment } from "../../environment";
import { errorCatcher, useSnackbar } from "../../hooks";
import { useLogin } from "../login";
import { INotificationOutbox, INotificationOutboxInput, notificationOutboxAtom, notificationOutboxLoadingAtom } from "./types";

type UseNotificationOutbox = [INotificationOutbox[], {
    list: (query?: Record<string, any>) => Promise<INotificationOutbox[] | null>;
    getById: (id: number) => Promise<INotificationOutbox | null>;
    getPaginated: (first?: number, offset?: number, query?: Record<string, any>) => Promise<INotificationOutbox[] | null>;
    create: (input: INotificationOutboxInput) => Promise<INotificationOutbox | null>;
    update: (id: number, input: INotificationOutboxInput) => Promise<INotificationOutbox | null>;
    remove: (id: number) => Promise<INotificationOutbox | null>;
}];

const gqlEndpoint = `${environment.baseUrl}/graphql`;
const graphqlRequest = async (authToken: string | null, query: string, variables?: any) => {
    const { data } = await axios.post(gqlEndpoint, { query, variables }, { headers: { 'X-Wallmates-Auth': authToken } });
    if (data.errors) throw new Error(data.errors[0]?.message || 'GraphQL error');
    return data.data;
}

export const useNotificationOutbox = (): UseNotificationOutbox => {
    const [items, setItems] = useAtom(notificationOutboxAtom);
    const setLoading = useSetAtom(notificationOutboxLoadingAtom);
    const [, setSnackbar] = useSnackbar();
    const [, authToken, loginFunctions] = useLogin();

    const list = async (query: Record<string, any> = {}) => {
        try { setLoading(true);
            const q = `query($query: JSON){ getNotificationOutboxes(query: $query){ id type payload status attempts created_at updated_at } }`;
            const data = await graphqlRequest(authToken, q, { query });
            setItems(data.getNotificationOutboxes);
            return data.getNotificationOutboxes as INotificationOutbox[];
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); setSnackbar({show:true,snackbarLevel:'error',text:'Failed to fetch outbox.'}); return null; }
        finally { setLoading(false);} };

    const getById = async (id: number) => {
        try { const q = `query($id: ID!){ getNotificationOutboxById(id:$id){ id type payload status attempts created_at updated_at } }`;
            const data = await graphqlRequest(authToken, q, { id });
            return data.getNotificationOutboxById as INotificationOutbox;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const getPaginated = async (first = 20, offset = 0, query: Record<string, any> = {}) => {
        try { const q = `query($first:Int,$offset:Int,$query:JSON){ getPaginatedNotificationOutboxes(first:$first,offset:$offset,query:$query){ id type payload status attempts created_at updated_at } }`;
            const data = await graphqlRequest(authToken, q, { first, offset, query });
            return data.getPaginatedNotificationOutboxes as INotificationOutbox[];
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const create = async (input: INotificationOutboxInput) => {
        try { const q = `mutation($input:NotificationOutboxInput!){ createNotificationOutbox(input:$input){ id } }`;
            const data = await graphqlRequest(authToken, q, { input });
            return data.createNotificationOutbox as INotificationOutbox;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const update = async (id: number, input: INotificationOutboxInput) => {
        try { const q = `mutation($id:ID!,$input:NotificationOutboxInput!){ updateNotificationOutbox(id:$id,input:$input){ id } }`;
            const data = await graphqlRequest(authToken, q, { id, input });
            return data.updateNotificationOutbox as INotificationOutbox;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const remove = async (id: number) => {
        try { const q = `mutation($id:ID!){ deleteNotificationOutbox(id:$id){ id } }`;
            const data = await graphqlRequest(authToken, q, { id });
            return data.deleteNotificationOutbox as INotificationOutbox;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    return [items, { list, getById, getPaginated, create, update, remove }];
}


